<template>
  <div class="pv-reporte">

    <!-- Encabezado -->
    <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
      <h5 class="pv-titulo mb-0">
        <i class="bi bi-box-seam me-2"></i>Productos Vendidos
      </h5>
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <span v-if="reporte" class="pv-periodo-badge">{{ reporte.periodo.etiqueta }}</span>
        <template v-if="reporte && !cargando">
          <button
            class="btn btn-sm btn-outline-success"
            @click="exportarXlsx"
            :disabled="exportandoXlsx"
            title="Descargar Excel"
          >
            <span v-if="exportandoXlsx" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-file-earmark-excel me-1"></i>Excel
          </button>
          <button
            class="btn btn-sm btn-outline-danger"
            @click="exportarPdf"
            :disabled="exportandoPdf"
            title="Descargar PDF"
          >
            <span v-if="exportandoPdf" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-file-earmark-pdf me-1"></i>PDF
          </button>
        </template>
      </div>
    </div>

    <!-- Panel filtros -->
    <div class="pv-panel mb-3">
      <div class="pv-panel__cuerpo">

        <!-- Toggle modo -->
        <div class="d-flex gap-2 mb-3">
          <button
            :class="['btn btn-sm', modoFiltro === 'anio' ? 'btn-primary' : 'btn-outline-secondary']"
            @click="modoFiltro = 'anio'"
          ><i class="bi bi-calendar2-range me-1"></i>Por año</button>
          <button
            :class="['btn btn-sm', modoFiltro === 'rango' ? 'btn-primary' : 'btn-outline-secondary']"
            @click="modoFiltro = 'rango'"
          ><i class="bi bi-calendar3 me-1"></i>Rango de fechas</button>
        </div>

        <!-- Selector por año -->
        <div v-if="modoFiltro === 'anio'" class="d-flex flex-wrap gap-2 align-items-center mb-3">
          <span class="pv-label-muted me-1">Seleccionar año:</span>
          <button
            v-for="a in aniosDisponibles"
            :key="a"
            :class="['btn btn-sm', anioSeleccionado === a ? 'btn-primary' : 'btn-outline-secondary']"
            @click="seleccionarAnio(a)"
            :disabled="cargando"
          >{{ a }}</button>
        </div>

        <!-- Rango personalizado -->
        <div v-if="modoFiltro === 'rango'" class="row g-2 align-items-end mb-3">
          <div class="col-sm-4 col-md-3">
            <label class="form-label form-label-sm mb-1">Fecha desde</label>
            <input type="date" class="form-control form-control-sm pv-input" v-model="filtros.fecha_desde" :max="filtros.fecha_hasta" />
          </div>
          <div class="col-sm-4 col-md-3">
            <label class="form-label form-label-sm mb-1">Fecha hasta</label>
            <input type="date" class="form-control form-control-sm pv-input" v-model="filtros.fecha_hasta" :min="filtros.fecha_desde" />
          </div>
          <div class="col-auto d-flex gap-1">
            <button class="btn btn-sm btn-primary" @click="generarReporte" :disabled="cargando">
              <span v-if="cargando" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-search me-1"></i>{{ cargando ? 'Generando…' : 'Generar' }}
            </button>
            <button class="btn btn-sm btn-link" style="color:var(--text-muted)" @click="limpiarRango" :disabled="cargando">
              Limpiar
            </button>
          </div>
        </div>

        <!-- Ordenar por -->
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <span class="pv-label-muted">Ordenar por:</span>
          <button
            v-for="op in opcionesOrden"
            :key="op.valor"
            :class="['btn btn-sm', ordenarPor === op.valor ? 'btn-primary' : 'btn-outline-secondary']"
            @click="cambiarOrden(op.valor)"
            :disabled="cargando"
          >{{ op.etiqueta }}</button>
        </div>

      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMensaje" class="pv-alerta pv-alerta--error d-flex align-items-center gap-2 mb-3">
      <i class="bi bi-x-circle-fill"></i>
      <span>{{ errorMensaje }}</span>
    </div>

    <!-- Cargando -->
    <div v-if="cargando" class="pv-estado-vacio">
      <span class="spinner-border spinner-border-sm me-2"></span>Generando reporte…
    </div>

    <template v-if="reporte && !cargando">

      <!-- KPI cards -->
      <div class="row g-3 mb-3">
        <div class="col-sm-6 col-xl-3">
          <div class="pv-kpi">
            <div class="pv-kpi__valor" style="color:var(--primary-color)">{{ reporte.resumen.productos_distintos }}</div>
            <div class="pv-kpi__etiqueta">Productos distintos</div>
          </div>
        </div>
        <div class="col-sm-6 col-xl-3">
          <div class="pv-kpi">
            <div class="pv-kpi__valor" style="color:var(--primary-color)">{{ formatearCantidad(reporte.resumen.unidades_vendidas) }}</div>
            <div class="pv-kpi__etiqueta">Unidades vendidas</div>
          </div>
        </div>
        <div class="col-sm-6 col-xl-3">
          <div class="pv-kpi">
            <div class="pv-kpi__valor" style="color:var(--accent-success)">{{ formatearMoneda(reporte.resumen.total_sin_iva) }}</div>
            <div class="pv-kpi__etiqueta">Total sin IVA</div>
          </div>
        </div>
        <div class="col-sm-6 col-xl-3">
          <div class="pv-kpi">
            <div class="pv-kpi__valor" style="color:var(--accent-success)">{{ formatearMoneda(reporte.resumen.total_con_iva) }}</div>
            <div class="pv-kpi__etiqueta">Total con IVA</div>
          </div>
        </div>
      </div>

      <!-- Resumen por año -->
      <div v-if="reporte.resumen_anual.length > 0" class="pv-panel mb-3">
        <div class="pv-panel__cabecera d-flex align-items-center justify-content-between">
          <span><i class="bi bi-bar-chart-line me-2"></i>Resumen por año</span>
        </div>
        <div class="table-responsive">
          <table class="pv-tabla">
            <thead>
              <tr>
                <th>Año</th>
                <th class="text-end">Productos dist.</th>
                <th class="text-end">Unidades</th>
                <th class="text-end">Total sin IVA</th>
                <th class="text-end">Total con IVA</th>
                <th class="text-end">Facturas</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="fila in reporte.resumen_anual"
                :key="fila.anio"
                class="pv-fila"
                :class="{ 'pv-fila--activa': esFilaActiva(fila.anio) }"
              >
                <td>
                  <span class="fw-semibold">{{ fila.anio }}</span>
                  <span v-if="esFilaActiva(fila.anio)" class="pv-badge-sel ms-1">seleccionado</span>
                </td>
                <td class="text-end">{{ fila.productos_distintos }}</td>
                <td class="text-end">{{ formatearCantidad(fila.unidades_vendidas) }}</td>
                <td class="text-end" style="color:var(--accent-success)">{{ formatearMoneda(fila.total_sin_iva) }}</td>
                <td class="text-end fw-semibold" style="color:var(--accent-success)">{{ formatearMoneda(fila.total_con_iva) }}</td>
                <td class="text-end">{{ fila.num_facturas }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Tabla de productos -->
      <div class="pv-panel mb-3">
        <div class="pv-panel__cabecera d-flex align-items-center justify-content-between">
          <span><i class="bi bi-list-ul me-2"></i>Detalle de productos — {{ reporte.periodo.etiqueta }}</span>
          <span class="pv-conteo">{{ reporte.productos.length }} producto(s)</span>
        </div>
        <div v-if="reporte.productos.length > 0" class="table-responsive">
          <table class="pv-tabla">
            <thead>
              <tr>
                <th style="width:40px">#</th>
                <th>Código</th>
                <th>Producto</th>
                <th>Categoría</th>
                <th>Tipo</th>
                <th class="text-end">Cantidad</th>
                <th class="text-end">P. Promedio</th>
                <th class="text-end">Subtotal</th>
                <th class="text-end">Total c/IVA</th>
                <th class="text-end">Facturas</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(p, idx) in reporte.productos"
                :key="idx"
                class="pv-fila"
              >
                <td class="text-center pv-muted">{{ idx + 1 }}</td>
                <td class="pv-muted">{{ p.codigo }}</td>
                <td class="fw-semibold">{{ p.nombre_producto }}</td>
                <td>{{ p.categoria }}</td>
                <td>
                  <span :class="['pv-tipo', p.tipo_producto === 'servicio' ? 'pv-tipo--servicio' : 'pv-tipo--bien']">
                    {{ p.tipo_producto }}
                  </span>
                </td>
                <td class="text-end">{{ formatearCantidad(p.cantidad_vendida) }}</td>
                <td class="text-end pv-muted">{{ formatearMoneda(p.precio_promedio) }}</td>
                <td class="text-end">{{ formatearMoneda(p.subtotal_sin_iva) }}</td>
                <td class="text-end fw-semibold" style="color:var(--accent-success)">{{ formatearMoneda(p.total_con_iva) }}</td>
                <td class="text-end pv-muted">{{ p.num_facturas }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="pv-pie">
                <td colspan="5">Total</td>
                <td class="text-end">{{ formatearCantidad(reporte.resumen.unidades_vendidas) }}</td>
                <td></td>
                <td class="text-end">{{ formatearMoneda(reporte.resumen.total_sin_iva) }}</td>
                <td class="text-end" style="color:var(--accent-success)">{{ formatearMoneda(reporte.resumen.total_con_iva) }}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else class="pv-estado-vacio">
          <i class="bi bi-box-seam fs-3 d-block mb-2"></i>
          No hay productos vendidos en este período.
        </div>
      </div>

    </template>

    <!-- Estado inicial -->
    <div v-if="!reporte && !cargando && !errorMensaje" class="pv-panel">
      <div class="pv-estado-vacio">
        <i class="bi bi-box-seam fs-2 d-block mb-2"></i>
        Selecciona un año o rango de fechas para generar el reporte.
      </div>
    </div>

  </div>
</template>

<script>
import api from '@/services/api'
import themeManager from '@/store/theme'
import * as XLSX from 'xlsx'
import { fechaIsoSistema } from '@/utils/fechaSistema'

export default {
  name: 'ClientReportesProductosVendidos',

  data() {
    const hoy = new Date()
    return {
      modoFiltro: 'anio',
      anioSeleccionado: hoy.getFullYear(),
      filtros: {
        fecha_desde: `${hoy.getFullYear()}-01-01`,
        fecha_hasta: fechaIsoSistema(hoy),
      },
      ordenarPor: 'cantidad',
      opcionesOrden: [
        { valor: 'cantidad', etiqueta: 'Cantidad' },
        { valor: 'total',    etiqueta: 'Total'    },
        { valor: 'nombre',   etiqueta: 'Nombre'   },
      ],
      cargando: false,
      errorMensaje: null,
      reporte: null,
      exportandoXlsx: false,
      exportandoPdf: false,
    }
  },

  mounted() {
    this.generarReporte()
  },

  computed: {
    themeClass() {
      return themeManager.themeClass
    },

    aniosDisponibles() {
      const actual = new Date().getFullYear()
      const set = new Set([actual])
      if (this.reporte?.resumen_anual) {
        this.reporte.resumen_anual.forEach(r => set.add(Number(r.anio)))
      }
      return Array.from(set).sort((a, b) => b - a)
    },
  },

  methods: {
    async generarReporte() {
      this.cargando = true
      this.errorMensaje = null
      this.reporte = null
      try {
        const params = new URLSearchParams()
        if (this.modoFiltro === 'anio') {
          params.set('anio', this.anioSeleccionado)
        } else {
          if (this.filtros.fecha_desde) params.set('fecha_desde', this.filtros.fecha_desde)
          if (this.filtros.fecha_hasta) params.set('fecha_hasta', this.filtros.fecha_hasta)
        }
        params.set('ordenar_por', this.ordenarPor)
        const { data } = await api.get(`/reportes/productos-vendidos?${params}`)
        if (data.success) {
          this.reporte = data
        } else {
          this.errorMensaje = data.error || 'No se pudieron obtener los datos.'
        }
      } catch (err) {
        this.errorMensaje = err?.response?.data?.error || 'Error al conectar con el servidor.'
      } finally {
        this.cargando = false
      }
    },

    seleccionarAnio(anio) {
      this.anioSeleccionado = anio
      this.generarReporte()
    },

    cambiarOrden(valor) {
      if (this.ordenarPor === valor) return
      this.ordenarPor = valor
      if (this.reporte) this.generarReporte()
    },

    limpiarRango() {
      const hoy = new Date()
      this.filtros.fecha_desde = `${hoy.getFullYear()}-01-01`
      this.filtros.fecha_hasta = fechaIsoSistema(hoy)
      this.reporte = null
      this.errorMensaje = null
    },

    esFilaActiva(anio) {
      return this.modoFiltro === 'anio' && Number(anio) === this.anioSeleccionado
    },

    formatearMoneda(valor) {
      return '$' + parseFloat(valor || 0).toFixed(2)
    },

    formatearCantidad(valor) {
      return parseFloat(valor || 0).toLocaleString('es-EC', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },

    // ── Exportar Excel ────────────────────────────────────────────────
    exportarXlsx() {
      if (!this.reporte) return
      this.exportandoXlsx = true
      try {
        const r   = this.reporte
        const wb  = XLSX.utils.book_new()

        // Hoja Resumen
        const resumen = [
          ['Reporte de Productos Vendidos'],
          ['Período', r.periodo.etiqueta],
          [],
          ['Concepto', 'Valor'],
          ['Productos distintos',  r.resumen.productos_distintos],
          ['Unidades vendidas',    r.resumen.unidades_vendidas],
          ['Total sin IVA',        r.resumen.total_sin_iva],
          ['Total con IVA',        r.resumen.total_con_iva],
        ]
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resumen), 'Resumen')

        // Hoja por año
        if (r.resumen_anual.length > 0) {
          const cabA = ['Año', 'Productos dist.', 'Unidades', 'Total sin IVA', 'Total con IVA', 'Facturas']
          const filaA = r.resumen_anual.map(f => [
            f.anio, f.productos_distintos, parseFloat(f.unidades_vendidas),
            parseFloat(f.total_sin_iva), parseFloat(f.total_con_iva), f.num_facturas,
          ])
          XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([cabA, ...filaA]), 'Por año')
        }

        // Hoja detalle
        const cabP = ['Código', 'Producto', 'Categoría', 'Tipo', 'Cantidad', 'P. Promedio', 'Subtotal', 'Total c/IVA', 'Facturas']
        const filaP = r.productos.map(p => [
          p.codigo, p.nombre_producto, p.categoria, p.tipo_producto,
          parseFloat(p.cantidad_vendida), parseFloat(p.precio_promedio),
          parseFloat(p.subtotal_sin_iva), parseFloat(p.total_con_iva),
          p.num_facturas,
        ])
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([cabP, ...filaP]), 'Productos')

        const etiq = r.periodo.etiqueta.replace(/[^a-z0-9]/gi, '_')
        XLSX.writeFile(wb, `productos_vendidos_${etiq}.xlsx`)
      } finally {
        this.exportandoXlsx = false
      }
    },

    // ── Exportar PDF ──────────────────────────────────────────────────
    async exportarPdf() {
      if (!this.reporte) return
      this.exportandoPdf = true
      try {
        const params = new URLSearchParams()
        if (this.modoFiltro === 'anio') {
          params.set('anio', this.anioSeleccionado)
        } else {
          if (this.filtros.fecha_desde) params.set('fecha_desde', this.filtros.fecha_desde)
          if (this.filtros.fecha_hasta) params.set('fecha_hasta', this.filtros.fecha_hasta)
        }
        params.set('ordenar_por', this.ordenarPor)
        const response = await api.get(
          `/reportes/productos-vendidos/pdf?${params}`,
          { responseType: 'blob' }
        )
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url  = URL.createObjectURL(blob)
        const a    = document.createElement('a')
        const etiq = this.reporte.periodo.etiqueta.replace(/[^a-z0-9]/gi, '_')
        a.href     = url
        a.download = `productos_vendidos_${etiq}.pdf`
        a.click()
        URL.revokeObjectURL(url)
      } catch {
        this.errorMensaje = 'No se pudo generar el PDF. Intente nuevamente.'
      } finally {
        this.exportandoPdf = false
      }
    },
  },
}
</script>

<style scoped>
/* ── Contenedor ── */
.pv-reporte {
  padding: 1.25rem;
}

.pv-titulo {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--primary-color);
}

.pv-periodo-badge {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--secondary-bg-soft);
  border: 1px solid var(--border-main);
  border-radius: 20px;
  padding: 0.25rem 0.75rem;
}

/* ── Panel ── */
.pv-panel {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.pv-panel__cuerpo {
  padding: 0.85rem 1rem;
}

.pv-panel__cabecera {
  padding: 0.6rem 0.85rem;
  background: var(--secondary-bg-soft);
  border-bottom: 1px solid var(--border-main);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
}

/* ── KPI ── */
.pv-kpi {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  box-shadow: var(--shadow-sm);
  text-align: center;
  padding: 1rem;
}

.pv-kpi__valor {
  font-size: 1.5rem;
  font-weight: 700;
}

.pv-kpi__etiqueta {
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
}

/* ── Alertas ── */
.pv-alerta {
  padding: 0.5rem 0.85rem;
  border-radius: 8px;
  font-size: 0.875rem;
  border: 1px solid;
}

.pv-alerta--error {
  background: var(--accent-danger-light);
  border-color: var(--accent-danger);
  color: var(--accent-danger);
}

/* ── Estado vacío ── */
.pv-estado-vacio {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-muted);
  font-size: 0.9rem;
}

/* ── Tabla ── */
.pv-tabla {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.pv-tabla thead tr {
  background: var(--secondary-bg-soft);
  border-bottom: 2px solid var(--border-main);
}

.pv-tabla thead th {
  padding: 0.6rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.pv-fila {
  border-bottom: 1px solid var(--border-main);
  transition: background 0.1s;
}

.pv-fila td {
  padding: 0.55rem 0.75rem;
  vertical-align: middle;
  color: var(--text-primary);
}

.pv-fila:hover {
  background: var(--table-hover);
}

.pv-fila--activa {
  background: var(--accent-info-light) !important;
  border-left: 3px solid var(--primary-color);
}

/* ── Pie tabla ── */
.pv-pie td {
  padding: 0.55rem 0.75rem;
  font-weight: 600;
  color: var(--text-primary);
  background: var(--secondary-bg-soft);
  border-top: 2px solid var(--border-main);
}

/* ── Auxiliares ── */
.pv-muted {
  color: var(--text-muted);
  font-size: 0.82rem;
}

.pv-label-muted {
  font-size: 0.82rem;
  color: var(--text-muted);
}

.pv-conteo {
  font-size: 0.75rem;
  font-weight: 600;
  color: #fff;
  background: var(--primary-color);
  border-radius: 20px;
  padding: 0.2rem 0.6rem;
}

.pv-badge-sel {
  font-size: 0.7rem;
  font-weight: 600;
  color: #fff;
  background: var(--primary-color);
  border-radius: 10px;
  padding: 0.1rem 0.45rem;
}

/* ── Tipo producto ── */
.pv-tipo {
  font-size: 0.72rem;
  font-weight: 600;
  border-radius: 10px;
  padding: 0.15rem 0.5rem;
  text-transform: capitalize;
}

.pv-tipo--bien {
  background: var(--accent-info-light, #e0f2fe);
  color: var(--primary-color);
}

.pv-tipo--servicio {
  background: var(--accent-success-light, #d1fae5);
  color: var(--accent-success);
}

/* ── Input ── */
.pv-input {
  background: var(--input-bg);
  color: var(--text-primary);
  border-color: var(--border-main);
}

.pv-input:focus {
  border-color: var(--border-focus);
  box-shadow: none;
}
</style>
